import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { PanelRightOpen } from "lucide-react";
import { api } from "../api/client.js";
import type { Message, Project, ThinkingMode, User } from "../api/types.js";
import type { ChatPageProps } from "../features/chat/ChatPage.js";
import { useChatController } from "../features/chat/useChatController.js";
import { useActiveRunState } from "../features/chat/useActiveRunState.js";
import { useThreadScroll, type ThreadScrollController } from "../features/chat/useThreadScroll.js";
import { useAssets } from "../features/assets/useAssets.js";
import { Sidebar } from "../features/sidebar/Sidebar.js";
import { SourceDrawer } from "../features/sources/SourceDrawer.js";
import { useSourceDrawer, type UseSourceDrawerState } from "../features/sources/useSourceDrawer.js";
import { useSkills } from "../features/skills/useSkills.js";
import type { SkillsPageProps } from "../features/skills/SkillsPage.js";
import { useSessions, type WorkspacePanelActions } from "../features/sessions/useSessions.js";
import { WorkspaceRouter } from "../features/workspace/WorkspaceRouter.js";
import type { WorkspaceView } from "../features/workspace/workspaceView.js";

function latestAssistant(messages: Message[]) {
  return [...messages].reverse().find((message) => message.role === "assistant") || null;
}

function pendingAskMessage(messages: Message[], isWelcome: boolean) {
  return !isWelcome
    ? [...messages].reverse().find((message) => message.role === "assistant" && message.meta?.askUserQuestion && (message.meta.askUserQuestionStatus || "pending") === "pending") || null
    : null;
}

export function WorkspaceShell({ initialUser, onLogout }: { initialUser: User; onLogout: () => void }) {
  const [user] = useState(initialUser);
  const [plusOpen, setPlusOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [thinkingMode, setThinkingMode] = useState<ThinkingMode>("thinking");
  const [workspaceView, setWorkspaceView] = useState<WorkspaceView>("chat");
  const [stopPending, setStopPending] = useState(false);
  const scrollRef = useRef<ThreadScrollController | null>(null);
  const sourceDrawerRef = useRef<UseSourceDrawerState | null>(null);
  const panelActionsRef = useRef<WorkspacePanelActions | null>(null);

  const sessions = useSessions({ scrollRef, sourceDrawerRef, panelActionsRef });
  const activeRuns = useActiveRunState({ sessions });
  const currentAssistant = latestAssistant(activeRuns.visibleMessages);
  const isChatView = workspaceView === "chat";
  const isWelcome = isChatView && !sessions.currentSession && activeRuns.visibleMessages.length === 0;
  const askMessage = pendingAskMessage(activeRuns.visibleMessages, isWelcome);
  const scroll = useThreadScroll({
    currentSessionId: sessions.currentSession?.id,
    isWelcome,
    pendingAskMessage: askMessage
  });
  scrollRef.current = scroll;

  const sourceDrawer = useSourceDrawer({ messages: activeRuns.visibleMessages, currentAssistant });
  sourceDrawerRef.current = sourceDrawer;

  const skills = useSkills();

  const chat = useChatController({
    sessions,
    activeRuns,
    sourceDrawer,
    scroll,
    selectedSkill: skills.selectedSkill,
    setSelectedSkill: skills.setSelectedSkill,
    thinkingMode,
    setPlusOpen
  });

  const assets = useAssets({
    currentProjectId: sessions.currentProjectId,
    currentSession: sessions.currentSession,
    setAssets: sessions.setAssets,
    setCitationSources: sessions.setCitationSources,
    setReferencePool: sessions.setReferencePool,
    setPrompt: chat.setPrompt,
    setPlusOpen,
    setDrawerTab: sourceDrawer.setDrawerTab,
    setDrawerOpen: sourceDrawer.setDrawerOpen
  });

  panelActionsRef.current = {
    closeFloatingPanels: () => {
      setPlusOpen(false);
    },
    closeAllPanels: () => {
      setPlusOpen(false);
      sourceDrawer.setDrawerOpen(false);
    },
    clearPrompt: () => {
      chat.setPrompt("");
    }
  };

  async function refreshShell() {
    const me = await api<{ projects: Project[] }>("/api/auth/me");
    sessions.setProjects(me.projects);
    await skills.loadSkills();
    await sessions.loadSessions();
  }

  async function logout() {
    await api("/api/auth/logout", { method: "POST", body: JSON.stringify({}) }).catch(() => undefined);
    onLogout();
  }

  function closeWorkspacePanels() {
    setPlusOpen(false);
    sourceDrawer.setDrawerOpen(false);
  }

  function showChatView() {
    setWorkspaceView("chat");
  }

  function showSearchView() {
    closeWorkspacePanels();
    setWorkspaceView("search");
  }

  function showSkillsView() {
    closeWorkspacePanels();
    setWorkspaceView("skills");
    if (!skills.packs.length && !skills.skillsLoading) void skills.loadSkills();
  }

  function showNewChat() {
    showChatView();
    sessions.showNewChat();
  }

  const activeRunSessionId = activeRuns.activeRun?.sessionId || activeRuns.liveRun?.sessionId || null;
  const representedRunSessionId = activeRunSessionId || activeRuns.recoveringRun?.sessionId || null;
  const currentSessionOwnsRun = Boolean(representedRunSessionId && sessions.currentSession?.id === representedRunSessionId);

  function focusActiveRunAnchor() {
    window.requestAnimationFrame(() => {
      const placeholder = document.querySelector<HTMLElement>("[data-active-run-placeholder]");
      const anchor = placeholder || document.querySelector<HTMLElement>(".message.assistant:last-of-type");
      anchor?.scrollIntoView({ block: "center" });
      placeholder?.focus();
    });
  }

  function returnToActiveRun() {
    if (!activeRunSessionId) return;
    showChatView();
    if (sessions.currentSession?.id === activeRunSessionId) {
      focusActiveRunAnchor();
      return;
    }
    void sessions.openSessionById(activeRunSessionId).then(() => focusActiveRunAnchor()).catch(() => undefined);
  }

  function handleStopRun() {
    if (stopPending) return;
    setStopPending(true);
    void chat.stopRun().finally(() => setStopPending(false));
  }

  function openSidebarSession(session: typeof sessions.sessions[number]) {
    showChatView();
    if (session.id === activeRunSessionId) {
      // 活动会话行走内部返回路径:不换 URL、不开新流、不打断运行。
      returnToActiveRun();
      return;
    }
    if ((chat.submitting || chat.running) && session.id === sessions.currentSession?.id) return;
    void sessions.openSession(session);
  }

  function changeSidebarProject(projectId: string) {
    showChatView();
    void sessions.loadProject(projectId);
  }

  const chatPageProps: ChatPageProps = {
    active: isChatView,
    threadRef: scroll.threadRef,
    askDockRef: scroll.askDockRef,
    isWelcome,
    isSessionLoading: sessions.isSessionLoading,
    messages: activeRuns.visibleMessages,
    citationSources: activeRuns.visibleCitationSources,
    referencePoolTotalCount: sessions.referencePoolTotalCount,
    expandedActivityMessageId: sessions.expandedActivityMessageId,
    setExpandedActivityMessageId: sessions.setExpandedActivityMessageId,
    onOpenCitation: sourceDrawer.openCitation,
    onOpenMessageSources: sourceDrawer.openMessageSources,
    onOpenReferencePool: sourceDrawer.openReferencePool,
    prompt: chat.prompt,
    setPrompt: chat.setPrompt,
    submitting: chat.submitting,
    running: Boolean(chat.running),
    plusOpen,
    setPlusOpen,
    onSubmit: chat.sendPrompt,
    onStop: handleStopRun,
    onSaveLinkAsset: assets.saveLinkAsset,
    onUploadAsset: assets.uploadAsset,
    onAttachRecent: assets.attachRecent,
    skills: skills.skills,
    onManageSkills: showSkillsView,
    selectedSkill: skills.selectedSkill,
    setSelectedSkill: skills.setSelectedSkill,
    thinkingMode,
    setThinkingMode,
    pendingAskMessage: askMessage,
    onSubmitAskUserQuestion: (message, answers) => { void chat.submitAskUserQuestion(message, answers); },
    onCancelAskUserQuestion: (message) => { void chat.cancelAskUserQuestion(message); },
    saveSourceAsAsset: assets.saveSourceAsAsset,
    activeRun: activeRuns.activeRun,
    recoveringRun: activeRuns.recoveringRun,
    currentSessionOwnsRun,
    hasStreamBufferForCurrentSession: activeRuns.ownsVisibleStreamBuffer,
    stopPending,
    onReturnToActiveRun: returnToActiveRun
  };

  const skillsPageProps: SkillsPageProps = {
    packs: skills.packs,
    loading: skills.skillsLoading,
    savingSlug: skills.skillSavingSlug,
    error: skills.skillsError,
    onToggle: (pack, enabled) => { void skills.toggleSkillPack(pack, enabled); }
  };

  useEffect(() => {
    refreshShell().catch(() => undefined);
  }, []);

  useLayoutEffect(() => {
    if (!sessions.scrollToBottomSignal) return;
    const container = scroll.threadScrollContainer();
    if (!container) return;
    scroll.scrollThreadToBottom("auto");
  }, [sessions.scrollToBottomSignal]);

  useEffect(() => {
    const closeFloatingPanels = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setPlusOpen(false);
      sourceDrawer.setDrawerOpen(false);
    };
    document.addEventListener("keydown", closeFloatingPanels);
    return () => document.removeEventListener("keydown", closeFloatingPanels);
  }, [sourceDrawer.setDrawerOpen]);

  return (
    <main className={`app-shell ${isChatView ? (isWelcome ? "welcome-mode" : "chat-active") : "workspace-view-active"} ${sidebarCollapsed ? "sidebar-collapsed" : ""} ${isChatView && sourceDrawer.drawerOpen ? "sources-open" : ""}`}>
      <Sidebar
        user={user}
        projects={sessions.projects}
        sessions={sessions.sessions}
        currentProjectId={sessions.currentProjectId}
        currentSession={sessions.currentSession}
        activeRunSessionId={activeRunSessionId}
        activeView={workspaceView}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={() => setSidebarCollapsed((old) => !old)}
        onNewSession={showNewChat}
        onOpenSearch={showSearchView}
        onOpenSkills={showSkillsView}
        onOpenSession={openSidebarSession}
        onRenameSession={(session) => { void sessions.renameSession(session); }}
        onMoveSession={(session, projectId) => { void sessions.moveSession(session, projectId); }}
        onTogglePinSession={(session) => { void sessions.togglePinSession(session); }}
        onDeleteSession={(session) => { void sessions.deleteSession(session); }}
        onRenameProject={(project) => { void sessions.renameProject(project); }}
        onDeleteProject={(project) => { void sessions.deleteProject(project); }}
        onAddProject={sessions.addProject}
        onProjectChange={changeSidebarProject}
        onLogout={logout}
      />

      <main className="workspace" id="workspace">
        <WorkspaceRouter workspaceView={workspaceView} chatPageProps={chatPageProps} skillsPageProps={skillsPageProps} />
      </main>

      {isChatView && (
        <>
          <button
            className="workspace-icon-button source-drawer-toggle"
            type="button"
            data-tooltip="显示引用资料"
            aria-controls="sourceDrawer"
            aria-expanded={sourceDrawer.drawerOpen}
            aria-label="显示引用资料侧边栏"
            title="打开引用资料"
            onClick={() => {
              setPlusOpen(false);
              if (sourceDrawer.drawerOpen) sourceDrawer.setDrawerOpen(false);
              else sourceDrawer.openMessageSources(currentAssistant?.id || null);
            }}
          >
            <PanelRightOpen size={19} />
          </button>

          <SourceDrawer
            open={sourceDrawer.drawerOpen}
            tab={sourceDrawer.drawerTab}
            setTab={sourceDrawer.setDrawerTab}
            onClose={() => sourceDrawer.setDrawerOpen(false)}
            currentMessage={sourceDrawer.sourceMessage}
            citationSources={activeRuns.visibleCitationSources}
            referencePool={sessions.referencePool}
            referencePoolTotalCount={sessions.referencePoolTotalCount}
            referencePoolNextCursor={sessions.referencePoolNextCursor}
            referencePoolLoading={sessions.referencePoolLoading}
            referencePoolLoaded={sessions.referencePoolLoaded}
            referencePoolError={sessions.referencePoolError}
            assets={sessions.assets}
            currentSessionId={sessions.currentSession?.id || null}
            citationTarget={sourceDrawer.citationTarget}
            loadReferencePoolPage={sessions.loadReferencePoolPage}
            saveSourceAsAsset={assets.saveSourceAsAsset}
          />
        </>
      )}
    </main>
  );
}
